import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Bookmark, BookOpen, Palette, Search, Settings, Target } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useSettings } from '@/lib/store';
import { isTajweed } from './fonts';
import { StarIcon } from '@/components/islamic/Ornaments';
import { alive, useProgress } from '@/lib/store';
import { Card, Empty, IconButton, LargeTitle, Segmented } from '@/components/ui';
import { CHAPTERS, JUZS, chapter, juzOfKey, parseKey } from './api';
import { HifzCard } from '@/features/hifz/HifzCard';

export function SurahIndex() {
  const [tab, setTab] = useState<'surah' | 'juz' | 'bookmarks'>('surah');
  const [q, setQ] = useState('');
  const lastRead = useProgress((s) => s.lastRead);
  const bookmarks = useProgress((s) => s.bookmarks);

  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return CHAPTERS;
    return CHAPTERS.filter((c) => String(c.id) === t || c.n.toLowerCase().replace(/[^a-z]/g, '').includes(t.replace(/[^a-z]/g, '')) || c.t.toLowerCase().includes(t) || c.ar.includes(q.trim()));
  }, [q]);

  const marks = alive(bookmarks).sort((a, b) => b.createdAt - a.createdAt);
  const mushaf = useSettings((s) => s.settings.mushaf);
  const update = useSettings((s) => s.update);
  const nav = useNavigate();
  const plans = alive(useProgress((s) => s.khatmahs));

  return (
    <div className="page-pad">
      <LargeTitle title="Qur'an" ar="القرآن الكريم" subtitle="Read · recite · reflect" right={<IconButton label="Settings" to="/settings"><Settings size={18} /></IconButton>} />

      {lastRead && (
        <div className="px-4 pb-4">
          <Card to={`/quran/page/${lastRead.page}`} className="flex items-center gap-4 bg-gradient-to-br from-accent/25 to-transparent">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-accent text-accent-ink"><BookOpen size={22} /></span>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold uppercase tracking-wide text-muted">Continue reading</div>
              <div className="truncate text-[17px] font-bold">{chapter(parseKey(lastRead.verseKey).surah).n} · {lastRead.verseKey}</div>
              <div className="text-[13px] text-muted">Page {lastRead.page} · Juz {juzOfKey(lastRead.verseKey)}</div>
            </div>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 px-4 pb-4">
        <Card onClick={() => { update({ mushaf: isTajweed(mushaf) ? 'madani-v2' : 'tajweed' }); if (!isTajweed(mushaf)) nav(lastRead ? `/quran/page/${lastRead.page}` : '/quran/1'); }} className="flex items-center gap-3 !p-3">
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-[var(--tj-ghunnah)] to-[var(--tj-madd246)] text-white"><Palette size={19} /></span>
          <span className="min-w-0"><span className="block text-[15px] font-semibold">Tajweed</span><span className="block text-[12px] text-muted">{isTajweed(mushaf) ? 'On · tap to turn off' : "Colour-coded Qur'an"}</span></span>
        </Card>
        <Card to="/khatmah" className="flex items-center gap-3 !p-3">
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-accent text-accent-ink"><Target size={19} /></span>
          <span className="min-w-0"><span className="block text-[15px] font-semibold">Khatmah plans</span><span className="block truncate text-[12px] text-muted">{plans.length ? `${plans.length} active · one āyah at a time` : 'Recurring khatmahs & goals'}</span></span>
        </Card>
        <HifzCard />
      </div>

      <div className="sticky top-0 z-20 space-y-3 bg-bg/90 px-4 pb-3 pt-[calc(var(--safe-top)+6px)] backdrop-blur-md">
        <label className="flex items-center gap-2 rounded-xl bg-fill px-3 py-2">
          <Search size={18} className="text-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search surah name or number" className="w-full bg-transparent text-[16px] outline-none placeholder:text-faint" />
        </label>
        <Segmented label="Browse by" value={tab} onChange={setTab} options={[{ value: 'surah', label: 'Surah' }, { value: 'juz', label: 'Juz' }, { value: 'bookmarks', label: 'Bookmarks' }]} />
      </div>

      {tab === 'surah' && (
        <ul className="mx-4 group">
          {list.map((c) => (
            <li key={c.id}>
              <Link to={`/quran/${c.id}`} className="row press">
                <span className="relative grid h-11 w-11 shrink-0 place-items-center text-gold">
                  <StarIcon size={46} strokeWidth={1.3} hollow className="opacity-80" />
                  <span className={`absolute font-bold tabular-nums leading-none text-ink ${c.id > 99 ? 'text-[12px] tracking-[-0.03em]' : 'text-[15px]'}`}>{c.id}</span>
                </span>
                <span className="min-w-0 flex-1 pl-1">
                  <span className="block truncate text-[16px] font-semibold">{c.n}</span>
                  <span className="block truncate text-[13px] text-muted">{c.t} · {c.v} āyāt · {c.place === 'M' ? 'Makki' : 'Madani'}</span>
                </span>
                <span className="font-arabic text-[22px] text-gold" dir="rtl">{c.ar}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {tab === 'juz' && (
        <div className="grid grid-cols-2 gap-3 px-4 sm:grid-cols-3">
          {JUZS.map((j) => {
            const { surah, ayah } = parseKey(j.first);
            return (
              <Card key={j.n} to={`/quran/${surah}?ayah=${ayah}`}>
                <div className="text-[13px] font-semibold uppercase tracking-wide text-muted">Juz {j.n}</div>
                <div className="mt-1 truncate text-[16px] font-semibold">{chapter(surah).n}</div>
                <div className="text-[13px] text-muted">{j.first} – {j.last}</div>
              </Card>
            );
          })}
        </div>
      )}

      {tab === 'bookmarks' && (marks.length ? (
        <ul className="mx-4 group">
          {marks.map((b) => {
            const { surah, ayah } = parseKey(b.verseKey);
            return (
              <li key={b.id}>
                <Link to={`/quran/${surah}?ayah=${ayah}`} className="row press">
                  <Bookmark size={18} className="text-gold" />
                  <span className="flex-1"><span className="block font-semibold">{chapter(surah).n}</span><span className="text-[13px] text-muted">Āyah {ayah}</span></span>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : <Empty icon={<Bookmark size={36} />} title="No bookmarks yet" body="Tap any āyah while reading to bookmark it." />)}
    </div>
  );
}
